import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Columns, Search, X, Filter, FileText, Trash2,
  ChevronDown, ChevronUp,
  LayoutGrid, CheckCircle, XCircle,
  TrendingUp, Hash, Tag, Package, Layers, Box,
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { shouldShowCardsToolbarSearch } from '../../shared/utils/listPageSearchVisibility';
import { getBrandLocale } from '../../shared/utils/brandCurrency';
import localApiAdapter from '../../core/services/localApiAdapter';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  countArticleActive,
  countArticleInactive,
  isArticleActiveStatus,
  matchesArticleStatusFilter,
  getArticleStatusLabel,
} from './articlesStatusUtils';
import {
  buildArticleStatusBadgeOptions,
  buildArticleStatusColumnFilterOptions,
  buildArticleStatusSelectOptions,
  getCatalogStatusBadgeStyle,
} from './articleCatalogStatusOptions';
import {buildBulkDeleteFilterAction,
  mapArticlesStatCards,
  buildArticlesStatusFilterOptions,
  buildArticlesImportExportStub,
  articlesStatusFilterLabel,
  createArticleStatusChangeHandler,
  QAWAEM_TABLE_ACTIONS_TH_CLASS,
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemColumnVisibility, QawaemTableStickyActionsTd,
  ArticlesConfigNotice,
  sliceArticlesListItems,
} from './articlesListPageUtils';
import { useArticlesConfig, useArticlesListViewMode } from './useArticlesConfig';
import { fetchCatalogProducts, useCatalogProductsRefreshListener } from './catalogProductOptions';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { AppConfirmDialog } from '../../shared/components/AppConfirmDialog';
import { useCrudDeleteConfirm } from '../../shared/hooks/useCrudDeleteConfirm';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { useSettings } from '../../core/contexts/SettingsContext';

const VARIANT_ATTR_ALIASES = {
  size: ['size', 'taille'],
  color: ['color', 'couleur'],
  model: ['model', 'modele', 'modèle'],
  barcode: ['barcode', 'code_barres', 'ean'],
};

const parseVariantAttributes = (raw) => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

const getVariantAttr = (attrs, key) => {
  for (const alias of VARIANT_ATTR_ALIASES[key] || [key]) {
    if (attrs[alias] != null && attrs[alias] !== '') return String(attrs[alias]);
  }
  return '';
};

const buildAttributesJson = (raw, variantAttrs) => {
  const attrs = parseVariantAttributes(raw);
  Object.values(VARIANT_ATTR_ALIASES).flat().forEach((alias) => {
    delete attrs[alias];
  });
  Object.entries(variantAttrs).forEach(([key, value]) => {
    const trimmed = String(value || '').trim();
    if (trimmed) attrs[key] = trimmed;
  });
  return Object.keys(attrs).length ? JSON.stringify(attrs) : '';
};

const VariantesPage = () => {
  const { t, i18n } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  const { settings } = useSettings();
  const p = (key, opts) => t(`modules.article.pages.variantesPage.${key}`, opts);
  const qawaem = isSaudiBrand();
  const articlesConfig = useArticlesConfig();
  const numberLocale = getBrandLocale(i18n.language);
  const formatQty = (qty) => parseFloat(qty || 0).toLocaleString(numberLocale);

  const [searchTerm, setSearchTerm] = useState('');
  const [variants, setVariants] = useState([]);
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [viewingVariant, setViewingVariant] = useState(null);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [viewMode, setViewMode] = useArticlesListViewMode(articlesConfig);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    parent_product_id: '',
    variant_number: '',
    name: '',
    sku: '',
    attributes_json: '',
    stock_qty: '',
    status: 'actif',
    notes: ''
  });
  const [variantAttrs, setVariantAttrs] = useState({
    size: '',
    color: '',
    model: '',
    barcode: ''
  });

  const [formTab, setFormTab] = useState('parent');

  const { dateRange, setDateRange } = useSaudiListDateRange();

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_variants');
  const [quickFilter, setQuickFilter] = useState('all');
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const {
    deleteConfirm,
    closeDeleteConfirm,
    openDeleteOne,
    openBulkDelete,
    takePendingIds,
  } = useCrudDeleteConfirm();
  const entityLabel = p('entity_label', { defaultValue: 'المتغير' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'متغير' });

  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    loadVariants();
    loadProducts();
  }, [refreshTrigger]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadVariants = async () => {
    try {
      setLoading(true);
      const response = await localApiAdapter.get('/api/articles/variantes');
      const data = response?.data ?? response;
      setVariants(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error loading variants:', error);
      setVariants([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      setProducts(await fetchCatalogProducts({ type: 'product' }));
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    }
  };

  useCatalogProductsRefreshListener(loadProducts);

  const generateVariantNumber = useCallback(() => {
    const prefix = getEntityPrefix(settings, 'variant', 'VAR');
    return generateEntityReference(prefix, variants, (v) => v.variant_number);
  }, [variants, settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      window.alert(p('alert_name_required'));
      return;
    }
    if (!formData.status) {
      window.alert(p('alert_status_required'));
      return;
    }

    try {
      setLoading(true);
      const variantNumber = (formData.variant_number || '').trim()
        || (articlesConfig.autoGenerateVariantReference !== false ? generateVariantNumber() : '');
      const payload = {
        ...formData,
        variant_number: variantNumber,
        attributes_json: buildAttributesJson(formData.attributes_json, variantAttrs),
        stock_qty: parseFloat(formData.stock_qty) || 0
      };
      if (editingVariant) {
        await localApiAdapter.put(`/api/articles/variantes/${editingVariant.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/api/articles/variantes', payload);
        notifyCreated(showNotify, entityLabel);
      }
      resetForm();
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error saving variant:', error);
      notifyError(showNotify, p('alert_save_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (variant) => {
    setEditingVariant(variant);
    const attrs = parseVariantAttributes(variant.attributes_json);
    setVariantAttrs({
      size: getVariantAttr(attrs, 'size'),
      color: getVariantAttr(attrs, 'color'),
      model: getVariantAttr(attrs, 'model'),
      barcode: getVariantAttr(attrs, 'barcode'),
    });
    setFormData({
      parent_product_id: variant.parent_product_id || '',
      variant_number: variant.variant_number || '',
      name: variant.name || '',
      sku: variant.sku || '',
      attributes_json: variant.attributes_json || '',
      stock_qty: variant.stock_qty?.toString() || '',
      status: variant.status || '',
      notes: variant.notes || ''
    });
    setFormTab('parent');
    setShowModal(true);
  };

  

  

  const handleDelete = (id) => openDeleteOne(id);

  const handleBulkDelete = () => {
    if (!selectedVariants.length) return;
    openBulkDelete(selectedVariants);
  };

  const executeDeleteConfirmed = async () => {
    const ids = takePendingIds();
    if (!ids.length) return;
    try {
      if (ids.length === 1) {
        await localApiAdapter.delete(`/api/articles/variantes/${ids[0]}`);
        notifyDeleted(showNotify, entityLabel);
        setRefreshTrigger(prev => prev + 1);
      } else {
        const deleteCount = ids.length;
        await Promise.all(ids.map(id => localApiAdapter.delete(`/api/articles/variantes/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
        setSelectedVariants([]);
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error deleting variant:', error);
        notifyError(showNotify, p('alert_save_error'));
    }
  };

  const handleView = (variant) => {
    setViewingVariant(variant);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({
      parent_product_id: '',
      variant_number: '',
      name: '',
      sku: '',
      attributes_json: '',
      stock_qty: '',
      status: 'actif',
      notes: ''
    });
    setVariantAttrs({ size: '', color: '', model: '', barcode: '' });
    setEditingVariant(null);
    setFormTab('parent');
    setShowModal(false);
  };

  const formTabs = useMemo(() => ([
    { id: 'parent', label: p('section_parent'), icon: Package },
    { id: 'stock', label: p('section_stock'), icon: Layers },
  ]), [p]);

  const handleSelectVariant = (id) => {
    setSelectedVariants(selectedVariants.includes(id)
      ? selectedVariants.filter(vId => vId !== id)
      : [...selectedVariants, id]
    );
  };

  const variantsArray = Array.isArray(variants) ? variants : [];
  const stats = {
    total: variantsArray.length,
    actives: countArticleActive(variantsArray),
    parentsDistinct: new Set(variantsArray.map(v => v.parent_product_id).filter(Boolean)).size,
    skus: variantsArray.filter(v => v.sku).length,
    stockTotal: variantsArray.reduce((sum, v) => sum + (parseFloat(v.stock_qty) || 0), 0)
  };

  const mainTabs = [
    { id: 'all_variants', name: p('tab_overview'), icon: LayoutGrid },
    { id: 'par_parent', name: p('tab_by_parent'), icon: Package },
    { id: 'par_statut', name: p('tab_by_status'), icon: TrendingUp }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'actif', name: p('filter_active'), icon: CheckCircle },
    { id: 'inactif', name: p('filter_inactive'), icon: XCircle }
  ];

  const variantsDistribution = {
    all_variants: {
      title: p('dist_overview'),
      data: [
        { name: p('dist_active'), count: stats.actives, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { name: p('dist_inactive'), count: countArticleInactive(variantsArray), color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { name: p('dist_with_stock'), count: stats.stockTotal, color: 'bg-gradient-to-r from-blue-500 to-blue-600' }
      ]
    },
    par_parent: {
      title: p('dist_by_parent'),
      data: Array.from(
        variantsArray.reduce((map, v) => {
          const key = v.parent_product_name || p('no_parent');
          map.set(key, (map.get(key) || 0) + 1);
          return map;
        }, new Map())
      ).slice(0, 5).map(([name, count], i) => ({
        name,
        count,
        color: ['bg-gradient-to-r from-blue-500 to-blue-600', 'bg-gradient-to-r from-purple-500 to-purple-600', 'bg-gradient-to-r from-amber-500 to-amber-600'][i % 3]
      }))
    },
    par_statut: {
      title: p('dist_by_status'),
      data: [
        { name: p('dist_active'), count: stats.actives, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { name: p('dist_inactive'), count: countArticleInactive(variantsArray), color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    }
  };

  const distributionInfo = variantsDistribution[mainViewMode] || variantsDistribution.all_variants;

  const baseFilteredVariants = useMemo(
    () =>
      variantsArray.filter((variant) => {
        if (dateRange.start || dateRange.end) {
          const createdDate = new Date(variant.created_at || 0);
          if (dateRange.start) {
            const startOfDay = new Date(dateRange.start);
            startOfDay.setHours(0, 0, 0, 0);
            if (createdDate < startOfDay) return false;
          }
          if (dateRange.end) {
            const endOfDay = new Date(dateRange.end);
            endOfDay.setHours(23, 59, 59, 999);
            if (createdDate > endOfDay) return false;
          }
        }

        const matchesSearch =
          !searchTerm ||
          variant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          variant.variant_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          variant.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          variant.parent_product_name?.toLowerCase().includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;
        if (!matchesArticleStatusFilter(variant.status, quickFilter)) return false;
        return true;
      }),
    [variantsArray, dateRange, searchTerm, quickFilter],
  );

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('variant_number', (r) => r.variant_number || ''),
      textColumnRule('name', (r) => r.name || ''),
      textColumnRule('parent_product_name', (r) => r.parent_product_name || ''),
      textColumnRule('sku', (r) => r.sku || ''),
      textColumnRule('barcode', (r) => getVariantAttr(parseVariantAttributes(r?.attributes_json), 'barcode') || r.barcode || ''),
      textColumnRule('stock_qty', (r) => formatQty(r.stock_qty)),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [numberLocale, t],
  );

  const filteredVariants = useMemo(
    () => applyColumnFilters(baseFilteredVariants, colFilters, columnFilterRules),
    [baseFilteredVariants, colFilters, columnFilterRules],
  );

  const filteredVariantIds = useMemo(() => filteredVariants.map((v) => v.id), [filteredVariants]);
  const displayedVariants = useMemo(
    () => sliceArticlesListItems(filteredVariants, articlesConfig.itemsPerPage),
    [filteredVariants, articlesConfig.itemsPerPage],
  );
  const allRowsSelected = filteredVariantIds.length > 0 && filteredVariantIds.every((id) => selectedVariants.includes(id));
  const someRowsSelected = selectedVariants.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedVariants([]);
    } else {
      setSelectedVariants(filteredVariantIds);
    }
  };

  const getStatusBadge = (status) => (
    <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${getCatalogStatusBadgeStyle(status)}`}>
      {isArticleActiveStatus(status) ? <CheckCircle size={14} /> : <XCircle size={14} />}
      {getArticleStatusLabel(status, t)}
    </span>
  );

  const STATUS_OPTIONS = useMemo(() => buildArticleStatusBadgeOptions(t), [t]);

  const statusColumnFilterOptions = useMemo(() => buildArticleStatusColumnFilterOptions(t), [t]);

  const articleStatusSelectOptions = useMemo(() => buildArticleStatusSelectOptions(t), [t]);

  const handleStatusChange = useCallback(
    createArticleStatusChangeHandler('/api/articles/variantes', () => setRefreshTrigger((prev) => prev + 1)),
    [],
  );

  const formatAttributes = (raw) => {
    if (!raw) return p('empty_dash');
    try {
      const parsed = parseVariantAttributes(raw);
      const displayKeys = Object.keys(parsed).filter(
        (key) => !Object.values(VARIANT_ATTR_ALIASES).flat().includes(key)
      );
      const parts = [
        ...['size', 'color', 'model'].map((key) => {
          const value = getVariantAttr(parsed, key);
          return value ? `${p(`field_${key}`)}: ${value}` : null;
        }).filter(Boolean),
        ...displayKeys.map((key) => `${key}: ${parsed[key]}`),
      ];
      if (parts.length) return parts.join(', ');
    } catch {
      /* keep raw */
    }
    return raw;
  };

  const getVariantBarcode = (variant) => getVariantAttr(parseVariantAttributes(variant?.attributes_json), 'barcode');

  const productOptions = products.map(p => ({
    value: p.id,
    label: p.reference ? `${p.name} (${p.reference})` : p.name,
    isDefault: true
  }));

  const qawaemStatusOptions = useMemo(
    () => buildArticlesStatusFilterOptions(p, t),
    [p, t],
  );

  const qawaemStatusLabel = articlesStatusFilterLabel(quickFilter, qawaemStatusOptions);

  const { columnVisibility, handleToggleColumn, showCol, visibleDataColCount } = useQawaemColumnVisibility([
    'variant_number', 'name', 'parent_product_name', 'sku', 'barcode', 'stock_qty', 'status',
  ]);

  const qawaemVariantColumns = useMemo(
    () => [
      { key: 'variant_number', label: p('col_variant_number'), checked: columnVisibility.variant_number !== false, required: true },
      { key: 'name', label: p('col_name'), checked: columnVisibility.name !== false, required: true },
      { key: 'parent_product_name', label: p('col_parent'), checked: columnVisibility.parent_product_name !== false },
      { key: 'sku', label: p('col_sku'), checked: columnVisibility.sku !== false },
      { key: 'barcode', label: p('col_barcode'), checked: columnVisibility.barcode !== false },
      { key: 'stock_qty', label: p('col_stock'), checked: columnVisibility.stock_qty !== false },
      { key: 'status', label: p('col_status'), checked: columnVisibility.status !== false },
      { key: 'actions', label: p('col_actions'), checked: true, required: true },
    ],
    [p, columnVisibility],
  );
  const qawaemVisibleVariantColumnCount = qawaemVariantColumns.filter((c) => c.checked).length;
  const tableColSpan = 1 + visibleDataColCount + 1;

  const listStats = useMemo(
    () => mapArticlesStatCards([
      { key: 'total', label: p('stat_total'), value: stats.total, icon: Columns, variant: 'copper' },
      { key: 'active', label: p('stat_active'), value: stats.actives, icon: CheckCircle, variant: 'forest' },
      { key: 'parents', label: p('stat_parents'), value: stats.parentsDistinct, icon: Package, variant: 'copper' },
      { key: 'stock', label: p('stat_stock_total'), value: stats.stockTotal, icon: Box, variant: 'forest' },
    ]),
    [stats, p],
  );

  const openNewVariantModal = () => {
    if (!articlesConfig.enableVariants) {
      alert(p('variants_disabled', { defaultValue: 'المتغيرات معطّلة من إعدادات الأصناف.' }));
      return;
    }
    setEditingVariant(null);
    setFormData({
      parent_product_id: '',
      variant_number: articlesConfig.autoGenerateVariantReference !== false ? generateVariantNumber() : '',
      name: '',
      sku: '',
      attributes_json: '',
      stock_qty: '',
      status: 'actif',
      notes: ''
    });
    setVariantAttrs({ size: '', color: '', model: '', barcode: '' });
    setFormTab('parent');
    setShowModal(true);
  };

  const advancedOptionsContent = (
    <div className="space-y-4 pt-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                mainViewMode === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {quickFilterButtons.map((btn) => (
            <button
              key={btn.id}
              onClick={() => setQuickFilter(btn.id)}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                quickFilter === btn.id
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <btn.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{btn.name}</span>
            </button>
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <h3 className="text-xl font-semibold text-white mb-4">{distributionInfo.title}</h3>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>
          {distributionInfo.data.map((item, index) => {
            const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
            const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
            return (
              <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-medium text-white">{item.name}</h4>
                  <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white`}>{item.count}</div>
                </div>
                <div className="w-full bg-white/10 h-3 rounded-full">
                  <div className={`h-3 rounded-full ${item.color}`} style={{ width: `${percentage}%` }} />
                </div>
                <p className="mt-2 text-sm text-white/60">{p('percent_total', { percent: percentage })}</p>
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );

  return (
    <>
      <ArticlesConfigNotice
        message={!articlesConfig.enableVariants ? p('notice_variants_disabled', { defaultValue: 'إدارة المتغيرات معطّلة — فعّلها من إعدادات الأصناف ← الفئات والمتغيرات.' }) : null}
      />
      <FacturationListPageLayout
        className="space-y-6"
        containerClassName="employees-container"
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Columns}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildBulkDeleteFilterAction({
          count: selectedVariants.length,
          onClick: handleBulkDelete,
          label: p('delete_count', { count: selectedVariants.length }),
          title: p('delete_count', { count: selectedVariants.length }),
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder')}
        onCreate={openNewVariantModal}
        createLabel={p('new_item')}
        importExport={buildArticlesImportExportStub({
          showImportDropdown,
          setShowImportDropdown,
          showExportDropdown,
          setShowExportDropdown,
          importDropdownRef,
          exportDropdownRef,
          p,
          t,
        })}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={
          !qawaem ? (
            <div className="facturation-list-controls-strip p-4 border-b border-white/10">
              <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
                {shouldShowCardsToolbarSearch(viewMode) ? (
                  <div className="relative min-w-0 flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      placeholder={p('search_placeholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ) : null}
                <FacturationViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  isLight={isDashboardLightMode}
                  cardsTitle={p('view_cards')}
                  tableTitle={p('view_table')}
                />
              </div>
            </div>
          ) : null
        }
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {showCol('variant_number') ? (
                    <TableColumnHeaderFilter
                      columnKey="variant_number"
                      label={p('col_variant_number')}
                      value={colFilters.variant_number ?? ''}
                      onChange={(v) => setColFilter('variant_number', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('name') ? (
                    <TableColumnHeaderFilter
                      columnKey="name"
                      label={p('col_name')}
                      value={colFilters.name ?? ''}
                      onChange={(v) => setColFilter('name', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('parent_product_name') ? (
                    <TableColumnHeaderFilter
                      columnKey="parent_product_name"
                      label={p('col_parent')}
                      value={colFilters.parent_product_name ?? ''}
                      onChange={(v) => setColFilter('parent_product_name', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('sku') ? (
                    <TableColumnHeaderFilter
                      columnKey="sku"
                      label={p('col_sku')}
                      value={colFilters.sku ?? ''}
                      onChange={(v) => setColFilter('sku', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('barcode') ? (
                    <TableColumnHeaderFilter
                      columnKey="barcode"
                      label={p('col_barcode')}
                      value={colFilters.barcode ?? ''}
                      onChange={(v) => setColFilter('barcode', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('stock_qty') ? (
                    <TableColumnHeaderFilter
                      columnKey="stock_qty"
                      label={p('col_stock')}
                      value={colFilters.stock_qty ?? ''}
                      onChange={(v) => setColFilter('stock_qty', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('status') ? (
                    <TableColumnHeaderFilter
                      columnKey="status"
                      label={p('col_status')}
                      kind="select"
                      options={statusColumnFilterOptions}
                      value={colFilters.status ?? ''}
                      onChange={(v) => setColFilter('status', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      align="center"
                      thClassName="text-center"
                    />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemVariantColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleVariantColumnCount}
                    totalCount={qawaemVariantColumns.length}
                    thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {loading ? (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60">{p('loading')}</td></tr>
                ) : displayedVariants.length > 0 ? (
                  displayedVariants.map((variant, index) => (
                    <tr
                      key={variant.id}
                      className={`hover:bg-white/5 transition-colors ${selectedVariants.includes(variant.id) ? 'bg-white/10' : ''}`}
                    >
                      <QawaemTableSelectRowCell
                        checked={selectedVariants.includes(variant.id)}
                        onChange={() => handleSelectVariant(variant.id)}
                      />
                      {showCol('variant_number') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{variant.variant_number || p('not_applicable')}</span>
                            <span className="text-white/50 text-xs mt-0.5">{formatAttributes(variant.attributes_json)}</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('name') ? (
                        <td className="px-6 py-4 text-white font-semibold">{variant.name}</td>
                      ) : null}
                      {showCol('parent_product_name') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{variant.parent_product_name || p('no_parent')}</span>
                            <span className="text-white/50 text-xs mt-0.5">{variant.parent_product_id || '—'}</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('sku') ? (
                        <td className="px-6 py-4 text-white/80 font-mono text-sm">{variant.sku || p('empty_dash')}</td>
                      ) : null}
                      {showCol('barcode') ? (
                        <td className="px-6 py-4 text-white/80 font-mono text-sm">{getVariantBarcode(variant) || p('empty_dash')}</td>
                      ) : null}
                      {showCol('stock_qty') ? (
                        <td className="px-6 py-4 text-white font-semibold">{formatQty(variant.stock_qty)}</td>
                      ) : null}
                      {showCol('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={variant.status}
                              recordId={variant.id}
                              options={STATUS_OPTIONS}
                              onChange={handleStatusChange}
                              title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                            />
                          </div>
                        </td>
                      ) : null}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => handleView(variant)}
                          onEdit={() => handleEdit(variant)}
                          onDelete={() => handleDelete(variant.id)}
                          viewTitle={p('action_view')}
                          editTitle={p('action_edit')}
                          deleteTitle={p('action_delete')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Columns size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm text-white/40 mt-2">{p('empty_hint')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {displayedVariants.length > 0 ? (
              displayedVariants.map((variant, index) => (
                <motion.div
                  key={variant.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4 flex-wrap gap-2">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Columns size={20} className="text-blue-400" />
                          {variant.name}
                        </h3>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300">{variant.variant_number}</span>
                        <QawaemClickableStatusBadge
                          status={variant.status}
                          recordId={variant.id}
                          options={STATUS_OPTIONS}
                          onChange={handleStatusChange}
                          title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-white/70">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_parent')}</h5>
                          <div>{variant.parent_product_name || p('no_parent')}</div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_sku_attrs')}</h5>
                          <div><span className="font-medium">{p('sku_label')}</span> {variant.sku || p('empty_dash')}</div>
                          <div className="mt-1"><span className="font-medium">{p('field_barcode')}:</span> {getVariantBarcode(variant) || p('empty_dash')}</div>
                          <div className="mt-1 text-xs">{formatAttributes(variant.attributes_json)}</div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_stock')}</h5>
                          <div className="text-white font-semibold text-lg">{formatQty(variant.stock_qty)}</div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_notes')}</h5>
                          <div className="text-xs">{variant.notes || p('empty_dash')}</div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(variant)}
                      onEdit={() => handleEdit(variant)}
                      onDelete={() => handleDelete(variant.id)}
                      viewTitle={p('action_view')}
                      editTitle={p('action_edit')}
                      deleteTitle={p('action_delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Columns size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {/* Add/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Columns className="h-6 w-6 text-blue-400" />
                {editingVariant ? p('modal_edit') : p('modal_new')}
              </h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="variant-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <CheckCircle size={18} />
                  {editingVariant ? p('btn_update') : p('btn_create')}
                </button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>

            <div className={`shrink-0 px-6 pt-3 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <div className="flex flex-wrap gap-1" role="tablist" aria-label={editingVariant ? p('modal_edit') : p('modal_new')}>
                {formTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = formTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setFormTab(tab.id)}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${
                        active
                          ? (isDashboardLightMode
                            ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px'
                            : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px')
                          : (isDashboardLightMode
                            ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80'
                            : 'text-white/55 hover:text-white hover:bg-white/5')
                      }`}
                    >
                      <TabIcon size={16} className={active ? (tab.id === 'stock' ? 'text-cyan-400' : 'text-emerald-400') : ''} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="variant-form" onSubmit={handleSubmit}>
                {formTab === 'parent' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_parent')}</label>
                      <CustomSelect
                        value={formData.parent_product_id}
                        onChange={(productId) => setFormData({ ...formData, parent_product_id: productId || '' })}
                        options={productOptions}
                        placeholder={p('field_parent_ph')}
                        searchable
                        creatable
                        autoSelect={false}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_variant_number')}</label>
                      <input
                        type="text"
                        value={formData.variant_number}
                        onChange={(e) => setFormData({ ...formData, variant_number: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                        placeholder={p('placeholder_variant_number', { defaultValue: 'VAR-001' })}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('field_name')} required /></label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder={p('field_name_ph')}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_status')}</label>
                      <CustomSelect
                        value={formData.status}
                        onChange={(val) => setFormData({ ...formData, status: val })}
                        options={articleStatusSelectOptions}
                        placeholder={p('field_status_ph')}
                        searchable={false}
                        creatable={false}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_sku')}</label>
                      <input
                        type="text"
                        value={formData.sku}
                        onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono text-sm"
                        placeholder={p('field_sku_ph')}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_barcode')}</label>
                      <input
                        type="text"
                        value={variantAttrs.barcode}
                        onChange={(e) => setVariantAttrs({ ...variantAttrs, barcode: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono text-sm"
                        placeholder={p('field_barcode_ph')}
                      />
                    </div>
                    <div className="min-w-0 md:col-span-3">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_notes')}</label>
                      <input
                        type="text"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder={p('field_notes_ph')}
                      />
                    </div>
                  </div>
                )}

                {formTab === 'stock' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_stock')}</label>
                      <CustomNumberInput
                        step={0.01}
                        value={formData.stock_qty}
                        onChange={(v) => setFormData({ ...formData, stock_qty: v })}
                        placeholder="0"
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_model')}</label>
                      <input
                        type="text"
                        value={variantAttrs.model}
                        onChange={(e) => setVariantAttrs({ ...variantAttrs, model: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder={p('field_model_ph')}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_size')}</label>
                      <input
                        type="text"
                        value={variantAttrs.size}
                        onChange={(e) => setVariantAttrs({ ...variantAttrs, size: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder={p('field_size_ph')}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_color')}</label>
                      <input
                        type="text"
                        value={variantAttrs.color}
                        onChange={(e) => setVariantAttrs({ ...variantAttrs, color: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder={p('field_color_ph')}
                      />
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Modal */}
      {showViewModal && viewingVariant && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Columns className="h-6 w-6 text-blue-400" />
                {viewingVariant.name}
              </h2>
              <button onClick={() => setShowViewModal(false)} className="text-white/60 hover:text-white transition-colors"><X className="h-6 w-6" /></button>
            </div>

            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Package size={18} className="text-emerald-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_identification')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_variant_number')}</p><p className="text-white font-semibold">{viewingVariant.variant_number || p('not_applicable')}</p></div>
                  <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_name')}</p><p className="text-white font-semibold">{viewingVariant.name}</p></div>
                  <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_parent')}</p><p className="text-white font-semibold">{viewingVariant.parent_product_name || p('no_parent')}</p></div>
                  <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('field_status')}</p><div className="mt-1">{getStatusBadge(viewingVariant.status)}</div></div>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Tag size={18} className="text-blue-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_details_view')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('field_sku')}</p><p className="text-white font-semibold font-mono">{viewingVariant.sku || p('not_applicable')}</p></div>
                  <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_barcode')}</p><p className="text-white font-semibold font-mono">{getVariantBarcode(viewingVariant) || p('not_applicable')}</p></div>
                  <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('field_stock')}</p><p className="text-white font-semibold">{formatQty(viewingVariant.stock_qty)}</p></div>
                  <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('field_size')}</p><p className="text-white font-semibold">{getVariantAttr(parseVariantAttributes(viewingVariant.attributes_json), 'size') || p('not_applicable')}</p></div>
                  <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('field_color')}</p><p className="text-white font-semibold">{getVariantAttr(parseVariantAttributes(viewingVariant.attributes_json), 'color') || p('not_applicable')}</p></div>
                  <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('field_model')}</p><p className="text-white font-semibold">{getVariantAttr(parseVariantAttributes(viewingVariant.attributes_json), 'model') || p('not_applicable')}</p></div>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Layers size={18} className="text-cyan-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_links')}</h3>
                </div>
                <div className="flex flex-wrap gap-3">
                  <a href="/mouvements-stock" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-200 border border-cyan-500/30 transition-all text-sm font-medium">
                    <Box size={16} />
                    {p('link_stock')}
                  </a>
                  <a href="/ventes/livre-des-ventes" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30 transition-all text-sm font-medium">
                    <TrendingUp size={16} />
                    {p('link_sales')}
                  </a>
                </div>
              </div>

              {viewingVariant.notes && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <FileText size={18} className="text-yellow-400" />
                    <h3 className="text-lg font-semibold text-white">{p('field_notes')}</h3>
                  </div>
                  <p className="text-white/70 text-sm italic">{viewingVariant.notes}</p>
                </div>
              )}
            </div>

            <button onClick={() => setShowViewModal(false)} className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10">
              {p('btn_close')}
            </button>
          </motion.div>
        </div>,
        document.body
      )}
            <AppConfirmDialog
        open={deleteConfirm.open}
        title={t('common.delete', 'حذف')}
        message={
          deleteConfirm.ids.length > 1
            ? p('bulk_delete_confirm', { count: deleteConfirm.ids.length })
            : p('delete_confirm')
        }
        confirmLabel={t('common.delete', 'حذف')}
        cancelLabel={t('common.cancel', 'إلغاء')}
        danger
        onCancel={closeDeleteConfirm}
        onConfirm={executeDeleteConfirmed}
      />
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default VariantesPage;
